import { FC, useCallback, useEffect, useRef } from 'react';

import { useFormContext } from 'react-hook-form';

import AdditionalAppliances from 'components/AdditionalAppliances/AdditionalAppliances';
import ArchesToTreat from 'components/ArchesToTreat';
import Auxiliaries from 'components/Auxiliaries/Auxiliaries';
import CheckboxGroupWithOtherTextBox from 'components/CheckBoxGroupWithOtherTextBox';
import Crowding from 'components/Crowding/Crowding';
import CrowdingExtraction from 'components/CrowdingExtraction';
import Midline from 'components/Midline/Midline';
import OverBite from 'components/OverBite/OverBite';
import RhfRadioGroup from 'components/RadioGroup/RhfRadioGroup/RhfRadioGroup';
import { RHFTextArea } from 'components/RHFTextArea/RHFTextArea';
import SagittalRelationship from 'components/SagittalRelationship/SagittalRelationship';
import Spacing from 'components/Spacing/Spacing';
import ToothAttachment from 'components/ToothAttachment';
import ToothInformation from 'components/ToothInformation';
import ToothMovementRestriction from 'components/ToothMovementRestriction';
import { ARCHES } from 'constants/prescriptionConstants';
import {
  ANGLE_CLASSIFICATION_OPTIONS,
  CHIEF_COMPLAINTS,
  MAIN_OBJECTIVES,
  OVERJET_OPTIONS,
  PATIENT_DIAGNOSIS_OPTIONS
} from 'constants/prescriptionOptions';
import { translate } from 'utils';

type Props = {
  isDisabled?: boolean;
  className?: string;
  onRadioButtonClick?: () => void;
};

const ULTRA_PREFIX = 'treatmentSpecifications.ZenClearUltra';

const TreatmentSpecificationsExternalUltra: FC<Props> = ({
  isDisabled,
  className = '',
  onRadioButtonClick
}) => {
  const { watch, setValue, getValues } = useFormContext();
  const watchArchesToTreat = watch(`${ULTRA_PREFIX}.archesToTreat.arches`);
  const previousArchesToTreat = useRef<string | undefined>();

  const resetToothChart = useCallback(
    (fieldName: string, position: string) => {
      const toothChart = getValues(fieldName);
      if (position === ARCHES.UPPER && toothChart) {
        setValue(fieldName, {
          ...getValues(fieldName),
          leftBottom: [],
          rightBottom: []
        });
      } else if (position === ARCHES.LOWER && toothChart) {
        setValue(fieldName, {
          ...getValues(fieldName),
          rightTop: [],
          leftTop: []
        });
      }
    },
    [getValues, setValue]
  );

  useEffect(() => {
    const isUpperOrLower = [ARCHES.LOWER, ARCHES.UPPER].includes(
      watchArchesToTreat
    );
    const hasArchChanged =
      previousArchesToTreat.current !== undefined &&
      previousArchesToTreat.current !== watchArchesToTreat;

    if (isUpperOrLower && hasArchChanged) {
      resetToothChart(
        `${ULTRA_PREFIX}.toothInformation.toothChart`,
        watchArchesToTreat
      );
      resetToothChart(
        `${ULTRA_PREFIX}.toothInformation.primaryToothChart`,
        watchArchesToTreat
      );
      resetToothChart(
        `${ULTRA_PREFIX}.toothMovementRestriction.toothChart`,
        watchArchesToTreat
      );
      resetToothChart(
        `${ULTRA_PREFIX}.toothMovementRestriction.primaryToothChart`,
        watchArchesToTreat
      );
      resetToothChart(
        `${ULTRA_PREFIX}.toothAttachment.toothChart`,
        watchArchesToTreat
      );
      resetToothChart(
        `${ULTRA_PREFIX}.extraction.toothChart`,
        watchArchesToTreat
      );
    }

    previousArchesToTreat.current = watchArchesToTreat;
  }, [watchArchesToTreat, resetToothChart]);

  return (
    <div className={className}>
      <div className='flex flex-col gap-y-10'>
        <div>
          <RhfRadioGroup
            inputProps={{ disabled: isDisabled }}
            title={translate('prescription.step2_angle_classification')}
            options={ANGLE_CLASSIFICATION_OPTIONS}
            name={`${ULTRA_PREFIX}.angleClassification`}
            className='mt-2 flex flex-col md:flex-row md:space-x-16'
            radioClassName='border-RADIO_BORDER'
            radioItemFont='md:text-17px text-sm'
            onClick={onRadioButtonClick}
          />
        </div>
        <CheckboxGroupWithOtherTextBox
          title={translate('prescription.step3_diagnosis')}
          isDisabled={isDisabled}
          checkBoxGroupName={`${ULTRA_PREFIX}.patientDiagnosis.diagnosis`}
          otherTextBoxName={`${ULTRA_PREFIX}.patientDiagnosis.otherText`}
          animateLabel='patientDiagnosis'
          options={PATIENT_DIAGNOSIS_OPTIONS}
        />
        <CheckboxGroupWithOtherTextBox
          title={translate('prescription.step4_chief_complaints')}
          isDisabled={isDisabled}
          checkBoxGroupName={`${ULTRA_PREFIX}.chiefComplaints.complaints`}
          otherTextBoxName={`${ULTRA_PREFIX}.chiefComplaints.otherText`}
          animateLabel='chiefComplaints'
          options={CHIEF_COMPLAINTS}
        />
        <CheckboxGroupWithOtherTextBox
          title={translate('prescription.step5_main_objectives')}
          isDisabled={isDisabled}
          checkBoxGroupName={`${ULTRA_PREFIX}.mainObjectives.objectives`}
          otherTextBoxName={`${ULTRA_PREFIX}.mainObjectives.otherText`}
          animateLabel='mainObjectives'
          options={MAIN_OBJECTIVES}
        />
        <ArchesToTreat isDisabled={isDisabled} />
        <ToothInformation
          isDisabled={isDisabled}
          fieldName={`${ULTRA_PREFIX}.toothInformation`}
          title={translate('prescription.step7_tooth_info')}
          animateLabel='toothInformation'
          isUpperDisabled={watchArchesToTreat === ARCHES.LOWER}
          isLowerDisabled={watchArchesToTreat === ARCHES.UPPER}
        />
        <ToothMovementRestriction
          isDisabled={isDisabled}
          fieldName={`${ULTRA_PREFIX}.toothMovementRestriction`}
          title={translate('prescription.step8_tooth_movement')}
          animateLabel='toothMovementRestriction'
          isUpperDisabled={watchArchesToTreat === ARCHES.LOWER}
          isLowerDisabled={watchArchesToTreat === ARCHES.UPPER}
          isToothChartRestricted={true}
        />
        <ToothAttachment
          isDisabled={isDisabled}
          fieldName={`${ULTRA_PREFIX}.toothAttachment`}
          title={translate('prescription.step9_tooth_attachment')}
          animateLabel='toothAttachment'
          isUpperDisabled={watchArchesToTreat === ARCHES.LOWER}
          isLowerDisabled={watchArchesToTreat === ARCHES.UPPER}
          isToothChartRestricted={true}
        />
        <SagittalRelationship isDisabled={isDisabled} />
        <div>
          <RhfRadioGroup
            inputProps={{ disabled: isDisabled }}
            title={translate('prescription.step11_overjet')}
            options={OVERJET_OPTIONS}
            name={`${ULTRA_PREFIX}.overjet`}
            className='flex flex-col md:flex-row md:space-x-16'
            radioClassName='border-RADIO_BORDER'
            radioItemFont='md:text-17px text-sm'
          />
        </div>
        <OverBite isDisabled={isDisabled} />
        <Midline isDisabled={isDisabled} />
        <Spacing isDisabled={isDisabled} />
        <Crowding isDisabled={isDisabled} />
        <CrowdingExtraction
          isDisabled={isDisabled}
          isUpperDisabled={watchArchesToTreat === ARCHES.LOWER}
          isLowerDisabled={watchArchesToTreat === ARCHES.UPPER}
        />
        <Auxiliaries
          isDisabled={isDisabled}
          isUpperDisabled={watchArchesToTreat === ARCHES.LOWER}
          isLowerDisabled={watchArchesToTreat === ARCHES.UPPER}
        />
        <AdditionalAppliances isDisabled={isDisabled} />
        <div>
          <RHFTextArea
            title={translate('prescription.step19_special_notes')}
            name={`${ULTRA_PREFIX}.specialNotes`}
            autogrow
            inputProps={{
              disabled: isDisabled,
              placeholder: translate('settings.please_type_here')
            }}
            maxgrowHeight={260}
          />
        </div>
      </div>
    </div>
  );
};

export default TreatmentSpecificationsExternalUltra;
